/**
 * Небольшие ячейки каталога пряников: значки файлов и связи с площадками.
 */

import { cn } from '../../../lib/cn'
import { Badge } from '../../../components/ui/Badge'
import { isLinkedToOzon, isLinkedToWb, isLinkedToYm } from '../../products-v2/lib'
import { COMPUTED_WEIGHT_HINT, fileFlags, formatWeightPair, isComputedWeight, KIND_LABEL } from '../lib'
import type { GingerbreadBlock, GingerbreadRow } from '../types'

/** JPG · PLT · STL: зелёный — файл есть, серый — нет. */
export function FileBadges({ block }: { block?: GingerbreadBlock | null }) {
  return (
    <span className="inline-flex items-center gap-1" aria-label="Файлы производства">
      {fileFlags(block).map((flag) => (
        <span
          key={flag.key}
          title={`${flag.hint}: ${flag.present ? 'есть' : 'нет'}`}
          aria-label={`${flag.label}: ${flag.present ? 'есть' : 'нет'}`}
          className={cn(
            'rounded px-1.5 py-0.5 font-mono text-2xs font-semibold',
            flag.present
              ? 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300'
              : 'bg-gray-100 text-gray-400 line-through decoration-gray-300 dark:bg-zinc-800 dark:text-zinc-500 dark:decoration-zinc-600',
          )}
        >
          {flag.label}
        </span>
      ))}
    </span>
  )
}

/** «51 г / 66,5 г»; расчётный вес (PP2) помечен и объяснён подсказкой. */
export function WeightCell({ block }: { block?: GingerbreadBlock | null }) {
  return (
    <span className="whitespace-nowrap text-[0.8125rem] tabular-nums">
      {formatWeightPair(block)}
      {isComputedWeight(block) && (
        <span
          title={COMPUTED_WEIGHT_HINT}
          className="ml-1.5 rounded bg-gray-100 px-1 py-0.5 text-2xs font-medium text-gray-500 dark:bg-zinc-800 dark:text-zinc-400"
        >
          расчётный
        </span>
      )}
    </span>
  )
}

export function KindBadge({ block }: { block?: GingerbreadBlock | null }) {
  if (!block) return <span className="text-gray-400 dark:text-zinc-500">—</span>
  return <Badge variant={block.kind === 'kit' ? 'purple' : 'blue'}>{KIND_LABEL[block.kind]}</Badge>
}

/** Точка «связан / нет» с идентификатором моноширинным шрифтом. */
export function LinkDot({ linked, id }: { linked: boolean; id?: string | number | null }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[0.8125rem]">
      <span
        className={cn(
          'inline-block h-2 w-2 shrink-0 rounded-full',
          linked ? 'bg-green-500' : 'bg-gray-300 dark:bg-zinc-600',
        )}
      />
      {linked ? (
        <span className="truncate font-mono text-2xs text-gray-600 dark:text-zinc-300">
          {id ? String(id) : 'связан'}
        </span>
      ) : (
        <span className="text-gray-400 dark:text-zinc-500">нет</span>
      )}
    </span>
  )
}

export function WbCell({ row }: { row: GingerbreadRow }) {
  return <LinkDot linked={isLinkedToWb(row)} id={row.wb_nm_id} />
}

export function OzonCell({ row }: { row: GingerbreadRow }) {
  return <LinkDot linked={isLinkedToOzon(row)} id={row.ozon_offer_id ?? row.ozon_product_id} />
}

export function YmCell({ row }: { row: GingerbreadRow }) {
  return <LinkDot linked={isLinkedToYm(row)} id={row.ym_market_sku} />
}
